import React from 'react';
import { VocabularyWord } from '../../types/Vocabulary';
import './VocabularyMatrix.css';

interface VocabularyIsland {
  category: string;
  words: VocabularyWord[];
  totalWords: number;
  conqueredWords: number;
  conquestPercentage: number;
  isConquered: boolean;
}

interface VocabularyIslandsProps {
  words: VocabularyWord[];
  onIslandClick: (category: string) => void;
}

const VocabularyIslands: React.FC<VocabularyIslandsProps> = ({ words, onIslandClick }) => {
  const calculateCoverage = (word: VocabularyWord): number => {
    return word.translations?.filter(t => t.language !== word.language).length || 0;
  };

  const hasFullCoverage = (word: VocabularyWord): boolean => {
    return calculateCoverage(word) === 3;
  };

  const groupWordsByCategory = (): VocabularyIsland[] => {
    const categoryMap = new Map<string, VocabularyWord[]>();

    words.forEach(word => {
      const category = word.category || 'Uncategorized';
      if (!categoryMap.has(category)) {
        categoryMap.set(category, []);
      }
      categoryMap.get(category)!.push(word);
    });

    const islands: VocabularyIsland[] = [];

    categoryMap.forEach((categoryWords, category) => {
      const totalWords = categoryWords.length;
      const conqueredWords = categoryWords.filter(hasFullCoverage).length;
      const conquestPercentage = totalWords > 0 ? (conqueredWords / totalWords) * 100 : 0;
      const isConquered = conquestPercentage >= 80;

      islands.push({
        category,
        words: categoryWords,
        totalWords,
        conqueredWords,
        conquestPercentage,
        isConquered
      });
    });

    return islands.sort((a, b) => {
      if (a.isConquered !== b.isConquered) {
        return a.isConquered ? 1 : -1;
      }
      return b.conquestPercentage - a.conquestPercentage;
    });
  };

  const islands = groupWordsByCategory();

  const handleIslandClick = (category: string) => {
    onIslandClick(category);
  };

  return (
    <div className="vocabulary-islands-container">
      <div className="vocabulary-islands-grid">
        {islands.map((island) => (
          <div
            key={island.category}
            className={`vocabulary-island ${island.isConquered ? 'conquered' : ''}`}
            onClick={() => handleIslandClick(island.category)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleIslandClick(island.category);
              }
            }}
          >
            <div className="vocabulary-island-header">
              <h3 className="vocabulary-island-name">{island.category}</h3>
              {island.isConquered && (
                <span className="vocabulary-island-conquered-badge">✓</span>
              )}
            </div>
            <div className="vocabulary-island-stats">
              <span className="vocabulary-island-progress-text">
                {island.conqueredWords} / {island.totalWords}
              </span>
              <div className="vocabulary-island-progress-bar">
                <div
                  className="vocabulary-island-progress-fill"
                  style={{ width: `${island.conquestPercentage}%` }}
                />
              </div>
            </div>
            <div className="vocabulary-island-footer">
              <span className="vocabulary-island-percentage">
                {Math.round(island.conquestPercentage)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VocabularyIslands;

